// People & companies the books deal with (shareholders, directors, staff,
// customers, suppliers, tenants). Changes go through acc_create_party /
// acc_update_party; a party used in the books can be deactivated, never deleted.
import { useState, useMemo, useEffect } from "react";
import { Modal, Button, Field, Input, Select, Notice, DataTable, ActionMenu, StatusBadge } from "./kit/components";
import { T, text } from "./kit/tokens";
import { showToast } from "../ui/core";
import { createParty, updateParty, createAccount } from "./api";
import { Chip, ROLE_LABELS, errText } from "./shared";
import { nextFreeCode } from "./ChartOfAccounts";
import PartyStatement from "./PartyStatement";

const ROLE_ORDER = ["shareholder", "director", "staff", "freelancer", "customer", "vendor", "tenant", "other"];

export default function Parties({ ctx }) {
    const { data, reload } = ctx;
    const [editing, setEditing] = useState(null);       // party or {} for new
    const [setupFor, setSetupFor] = useState(null);     // shareholder needing loan/advance accounts
    const [roleFilter, setRoleFilter] = useState("");
    const [statementFor, setStatementFor] = useState(null);   // 2D-2: read-only party statement
    const rolesOf = useMemo(() => {
        const m = {}; data.roles.forEach(r => { (m[r.party_id] ||= []).push(r.role); }); return m;
    }, [data.roles]);
    const staffName = useMemo(() => Object.fromEntries(data.staff.map(s => [s.id, s.name])), [data.staff]);
    const tenantName = useMemo(() => Object.fromEntries(data.tenants.map(t => [t.id, t.name])), [data.tenants]);
    const ownAccounts = (p) => data.accounts.filter(a => a.fixed_party_id === p.id);
    const rows = data.parties.filter(p => !roleFilter || (rolesOf[p.id] || []).includes(roleFilter));

    const columns = [
        { key: "name", header: "Name", sortValue: r => r.name.toLowerCase(), render: r => (
            <div><div style={{ color: T.textStrong, fontWeight: 600 }}>{r.name}</div>
                {(r.staff_id || r.tenant_id) && <div style={text.caption}>{r.staff_id ? `Staff record: ${staffName[r.staff_id] || r.staff_id}` : `Tenant record: ${tenantName[r.tenant_id] || "—"}`}</div>}
            </div>) },
        { key: "roles", header: "Roles", render: r => (rolesOf[r.id] || []).sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b)).map(x => <Chip key={x}>{ROLE_LABELS[x]}</Chip>) },
        { key: "accounts", header: "Own accounts", hideOnMobile: true, render: r => { const a = ownAccounts(r); return a.length ? a.map(x => <div key={x.id} style={text.caption}>{x.code} {x.name}</div>) : <span style={text.caption}>—</span>; } },
        { key: "pan", header: "PAN", render: r => r.pan || <span style={text.caption}>—</span> },
        { key: "active", header: "Status", render: r => <StatusBadge status={r.active ? "confirmed" : "draft"} label={r.active ? "Active" : "Inactive"} /> },
    ];

    return (
        <div>
            <Notice tone="info">Everyone the company owes money to or is owed money by: shareholders and directors (loans and advances), staff (advances, payable salary), customers and suppliers. Each shareholder has separate loan and advance accounts so their balances never mix.</Notice>
            <div style={{ height: 16 }} />
            <DataTable columns={columns} rows={rows} searchText={r => `${r.name} ${r.pan || ""} ${(rolesOf[r.id] || []).join(" ")}`}
                searchPlaceholder="Search people & companies…" defaultSort={{ key: "name", dir: "asc" }}
                empty="No people or companies yet. Add the shareholders first."
                filters={<Select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ width: "auto", minWidth: 150 }}>
                    <option value="">All roles</option>{ROLE_ORDER.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </Select>}
                toolbarRight={<Button size="sm" onClick={() => setEditing({})}>Add person or company</Button>}
                rowActions={r => {
                    const isOwner = (rolesOf[r.id] || []).some(x => x === "shareholder" || x === "director");
                    return <ActionMenu items={[
                        { label: "Statement", onClick: () => setStatementFor(r) },
                        { label: "Edit", onClick: () => setEditing(r) },
                        isOwner && r.active && missingOwnerAccounts(data.accounts, r).length > 0 && { label: "Set up loan & advance accounts", onClick: () => setSetupFor(r) },
                    ]} />;
                }} />
            <PartyModal party={editing} ctx={ctx} roles={editing?.id ? rolesOf[editing.id] || [] : []} onClose={() => setEditing(null)}
                onDone={async () => { setEditing(null); await reload(); }} />
            <OwnerAccountsModal party={setupFor} ctx={ctx} onClose={() => setSetupFor(null)} onDone={async () => { setSetupFor(null); await reload(); }} />
            {statementFor && <PartyStatement party={statementFor} ctx={ctx} onClose={() => setStatementFor(null)}
                onOpenEntry={(id) => { setStatementFor(null); ctx.openEntry?.(id); }} />}
        </div>
    );
}

function PartyModal({ party, ctx, roles: currentRoles, onClose, onDone }) {
    const { data } = ctx;
    const isNew = party && !party.id;
    const [name, setName] = useState("");
    const [roles, setRoles] = useState([]);
    const [staffId, setStaffId] = useState("");
    const [tenantId, setTenantId] = useState("");
    const [pan, setPan] = useState("");
    const [contact, setContact] = useState("");
    const [active, setActive] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => {
        if (!party) return;
        setName(party.name || ""); setRoles(currentRoles); setStaffId(party.staff_id || ""); setTenantId(party.tenant_id || "");
        setPan(party.pan || ""); setContact(party.contact || ""); setActive(party.active !== false); setErr(""); setBusy(false);
    }, [party]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!party) return null;
    const linkedStaff = new Set(data.parties.map(p => p.staff_id).filter(Boolean));
    const linkedTenants = new Set(data.parties.map(p => p.tenant_id).filter(Boolean));
    const toggle = (r) => setRoles(rs => rs.includes(r) ? rs.filter(x => x !== r) : [...rs, r]);
    const save = async () => {
        setErr("");
        if (!name.trim()) { setErr("Enter a name."); return; }
        if (!roles.length) { setErr("Choose at least one role."); return; }
        if (pan && !/^\d{9}$/.test(pan)) { setErr("A PAN has 9 digits."); return; }
        setBusy(true);
        try {
            if (isNew) await createParty({ name: name.trim(), roles, staffId, tenantId, pan, contact: contact.trim() });
            else await updateParty(party.id, { name: name.trim(), pan, contact: contact.trim(), active, roles });
            showToast(isNew ? "Added" : "Saved");
            await onDone();
        } catch (e) { setErr(errText(e)); setBusy(false); }
    };
    return (
        <Modal open title={isNew ? "Add person or company" : `Edit ${party.name}`} onClose={busy ? undefined : onClose}
            footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? "Saving…" : isNew ? "Add" : "Save"}</Button></>}>
            <div style={{ display: "grid", gap: 14 }}>
                {isNew && (
                    <Field label="Link to a staff record (optional)" hint="Links this party to the person's existing staff record. Once linked, that staff record can't be deleted (it can still be marked as a former employee).">
                        <Select value={staffId} onChange={e => { setStaffId(e.target.value); setTenantId(""); const s = data.staff.find(x => x.id === e.target.value); if (s && !name) setName(s.name); }}>
                            <option value="">Not a staff member</option>
                            {data.staff.filter(s => !s.is_test_account || s.id === staffId).map(s => <option key={s.id} value={s.id} disabled={linkedStaff.has(s.id)}>{s.name}{linkedStaff.has(s.id) ? " (already added)" : ""}</option>)}
                        </Select>
                    </Field>
                )}
                {isNew && !staffId && data.tenants.length > 0 && (
                    <Field label="Link to a tenant record (optional)">
                        <Select value={tenantId} onChange={e => { setTenantId(e.target.value); const t = data.tenants.find(x => x.id === e.target.value); if (t && !name) setName(t.name); }}>
                            <option value="">Not a tenant</option>
                            {data.tenants.map(t => <option key={t.id} value={t.id} disabled={linkedTenants.has(t.id)}>{t.name}</option>)}
                        </Select>
                    </Field>
                )}
                <Field label="Name" required><Input value={name} onChange={e => setName(e.target.value)} /></Field>
                <div>
                    <span style={text.label}>Roles <span style={{ color: T.danger }}>*</span></span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {ROLE_ORDER.map(r => (
                            <label key={r} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14, color: T.text, border: `1px solid ${roles.includes(r) ? T.primary : T.border}`,
                                borderRadius: 8, padding: "6px 10px", cursor: "pointer" }}>
                                <input type="checkbox" checked={roles.includes(r)} onChange={() => toggle(r)} /> {ROLE_LABELS[r]}
                            </label>
                        ))}
                    </div>
                </div>
                <Field label="PAN (optional)" hint="9 digits"><Input value={pan} inputMode="numeric" maxLength={9} onChange={e => setPan(e.target.value.replace(/\D/g, ""))} /></Field>
                <Field label="Contact (optional)"><Input value={contact} onChange={e => setContact(e.target.value)} /></Field>
                {!isNew && (
                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, color: T.text }}>
                        <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> Active (inactive parties can't be used in new entries; history is kept)
                    </label>
                )}
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}

// Which of the person's own accounts (loan under 2410, advance under 1320) don't exist yet.
function missingOwnerAccounts(accounts, party) {
    const parentOf = (code) => accounts.find(a => a.code === code)?.id;
    const has = (code) => accounts.some(a => a.fixed_party_id === party.id && a.parent_id === parentOf(code));
    return ["2410", "1320"].filter(code => !has(code));
}

// Creates "Loan – <name>" and "Advance – <name>" accounts that only this person can use.
function OwnerAccountsModal({ party, ctx, onClose, onDone }) {
    const { data } = ctx;
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => { setErr(""); setBusy(false); }, [party]);
    if (!party) return null;
    const loanParent = data.accounts.find(a => a.code === "2410");
    const advParent = data.accounts.find(a => a.code === "1320");
    const loanCode = loanParent && nextFreeCode(data.accounts, loanParent);
    const advCode = advParent && nextFreeCode(data.accounts, advParent);
    const missing = missingOwnerAccounts(data.accounts, party);
    const ready = loanParent && advParent && loanCode && advCode;
    const save = async () => {
        setBusy(true); setErr("");
        try {
            if (missing.includes("2410")) await createAccount({ code: loanCode, name: `Loan – ${party.name}`, type: "liability", parentCode: "2410", isPostable: true, requiresParty: true, fixedPartyId: party.id, cashFlowClass: "financing",
                description: `Money ${party.name} has lent to the company` });
            if (missing.includes("1320")) await createAccount({ code: advCode, name: `Advance – ${party.name}`, type: "asset", parentCode: "1320", isPostable: true, requiresParty: true, fixedPartyId: party.id, cashFlowClass: "financing",
                description: `Money the company has advanced to ${party.name}` });
            showToast("Accounts created");
            await onDone();
        } catch (e) { setErr(errText(e)); setBusy(false); }
    };
    return (
        <Modal open title={`Loan & advance accounts for ${party.name}`} onClose={busy ? undefined : onClose}
            footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={save} disabled={busy || !ready}>{busy ? "Creating…" : missing.length === 2 ? "Create both accounts" : "Create the missing account"}</Button></>}>
            <div style={{ display: "grid", gap: 12, fontSize: 14, color: T.text, lineHeight: 1.5 }}>
                <div>Two accounts that only {party.name} can be used with, so their loans to the company and advances from it are always traceable on their own:</div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {missing.includes("2410") && <li><b>{loanCode} Loan – {party.name}</b> (owed by the company, under 2410)</li>}
                    {missing.includes("1320") && <li><b>{advCode} Advance – {party.name}</b> (owed to the company, under 1320)</li>}
                </ul>
                <div style={text.caption}>No balances are entered. Opening balances come later from the auditor-approved figures.</div>
                {!ready && <Notice tone="warning">The 2410 / 1320 headings are missing or full.</Notice>}
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}
